import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../utils/cn";

export interface SwitchProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange" | "value"
> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

/**
 * An on/off toggle: a knob that slides between the two states.
 *
 * Same controlled API as Checkbox (`checked` + `onCheckedChange`), so the two
 * are interchangeable at a call site. Use this where the setting takes effect
 * immediately and reads as on/off; use Checkbox where it is one of several
 * things being selected, or where a form is submitted afterwards.
 *
 * Hand-rolled rather than Radix, unlike Checkbox: the kit has no
 * @radix-ui/react-switch and `role="switch"` on a button is the whole
 * behaviour — pressing it toggles `aria-checked`, which is what a screen
 * reader announces.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onCheckedChange, className, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent p-0.5",
        "transition-colors focus-visible:outline-none focus-visible:ring-2",
        "focus-visible:ring-primary focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-primary" : "bg-muted border-border",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          "block h-4 w-4 rounded-full bg-background shadow-sm transition-transform",
          checked ? "translate-x-4" : "translate-x-0",
        )}
      />
    </button>
  ),
);
Switch.displayName = "Switch";
